import { useApiQuery } from '~/hooks';
import {
  getValidationTasksApi,
  getValidationTaskDetailsApi,
  ValidationTaskDetails,
} from '../repository';
import { keepPreviousData, UseQueryOptions } from '@tanstack/react-query';

export const VALIDATION_QUERY_KEYS = {
  VALIDATION_TASKS: 'validation-tasks',
  VALIDATION_TASK_DETAILS: 'validation-task-details',
};

/**
 * Query hook to fetch user's validation tasks
 * @param params - Query parameters (status, limit, offset)
 * @param options - Additional query options
 * @returns React Query result with validation tasks
 */
export const useGetValidationTasksQuery = (
  params?: {
    status?: string;
    limit?: number;
    offset?: number;
  },
  options?: UseQueryOptions<{
    tasks: ValidationTask[];
    total: number;
  }>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [VALIDATION_QUERY_KEYS.VALIDATION_TASKS, params],
    queryFn: () => getValidationTasksApi(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Query hook to fetch validation task details with contacts
 * @param taskId - Validation task ID
 * @param params - Query parameters (page, limit, type)
 * @param options - Additional query options
 * @returns React Query result with validation task details
 */
export const useGetValidationTaskDetailsQuery = (
  taskId: string,
  params?: {
    page?: number;
    limit?: number;
    type?: 'EMAIL' | 'PHONE';
  },
  options?: Omit<
    UseQueryOptions<ValidationTaskDetails>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useApiQuery({
    ...options,
    queryKey: [VALIDATION_QUERY_KEYS.VALIDATION_TASK_DETAILS, taskId, params],
    queryFn: () => getValidationTaskDetailsApi(taskId, params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled !== false && !!taskId,
  });
};
